import type { Messages } from "./vi";

/**
 * English translations.
 */
export const en: Messages = {
  app: {
    importTitle: "Excel Import",
    printTitle: "Print Export",
    enterpriseEdition: "Printer",
    navPrintExport: "Print Export",
    navImportData: "Import Data",
    language: "Language",
  },
  common: {
    back: "Back",
    continue: "Continue",
    cancel: "Cancel",
    save: "Save",
    reset: "Reset",
    dismiss: "Dismiss",
    error: "Error",
    none: "— None —",
    required: "Required",
    row: "Row",
    rows: "rows",
    columns: "columns",
    page: "Page",
    pages: "pages",
    of: "of",
    more: "more",
    skipped: "skipped",
    portrait: "Portrait",
    landscape: "Landscape",
  },
  importPage: {
    importComplete: "Import Complete",
    importedRows: "Successfully imported {count} row(s).",
    failedRows: "{count} row(s) failed.",
  },
  importWizard: {
    steps: {
      upload: "Upload",
      sheet: "Sheets",
      mapping: "Mapping",
      validate: "Validate",
      preview: "Preview",
      import: "Import",
    },
    parsingWorkbook: "Parsing workbook...",
    parseError: "Parse Error",
    fileLoaded: "File loaded successfully",
    sheetsRows: "{sheets} sheet(s) · {rows} rows total.",
    validationResults: "Validation Results",
    revalidate: "Re-validate",
    fixErrorsFirst: "Fix Errors First",
    previewImport: "Preview Import",
    importPreview: "Import Preview",
    reviewBeforeCommit:
      "Review the data before committing the import. {count} rows will be imported.",
    importData: "Import Data",
    readyToImport: "Ready to Import",
    rowsWillBeSubmitted: "{count} rows will be submitted to the server.",
    importing: "Importing...",
    confirmImport: "Confirm Import",
    backToPreview: "Back to Preview",
    sendingToServer: "Sending data to server...",
    importFailed: "Import Failed",
    importSuccessful: "Import Successful",
    rowsImported: "{count} row(s) imported successfully.",
    startOver: "Start Over",
    validate: "Validate",
  },
  upload: {
    title: "Upload Excel File",
    description:
      "Supports .xlsx, .xls, and .csv files up to {max} MB. Drag and drop or click to browse.",
    dragDrop: "Drag & drop your Excel file",
    dropHere: "Drop your file here",
    orBrowse: "or click to browse files",
    unsupportedType:
      "Unsupported file type. Please upload an .xlsx, .xls, or .csv file.",
    tooLarge: "File is too large. Maximum size is {max} MB.",
  },
  template: {
    selectTitle: "Select Import Template",
    selectDesc: "Choose the template that matches your Excel file structure.",
    sheetsColumns: "{sheets} sheet(s) · {columns} columns",
    required: "(required)",
    configured: "{label} — {sheets} sheet(s) configured.",
    formulaEnabled: "(formula preservation enabled)",
    mapSheets: "Map Sheets",
    mapSheetsDesc:
      "Match each template sheet to the corresponding sheet in your workbook.",
    requiredNotMatched: "A required sheet is not matched.",
    selectSheet: "— Select sheet —",
    employee: "Employee Import",
    product: "Product Catalog",
    inventory: "Inventory Import",
  },
  mapping: {
    title: "Column Mapping",
    description:
      "Match template columns to your workbook columns. Auto-matches are highlighted.",
    loadProfile: "Load saved profile",
    resetToAuto: "Reset to auto",
    reset: "Reset",
    saveProfile: "Save Profile",
    autoMapped: "{count} auto-mapped",
    manual: "manual",
    unmapped: "{count} unmapped",
    auto: "auto",
    requiredMark: "* required",
    selectColumn: "Select workbook column",
    notMapped: "— Not mapped —",
    saveProfileTitle: "Save Mapping Profile",
    profilePlaceholder: "Profile name (e.g. Monthly Employee Import)",
    savedProfiles: "Saved Profiles",
    unmappedHint:
      "{count} required column(s) not yet mapped. Map them before proceeding to validation.",
  },
  validation: {
    totalRows: "Total Rows",
    valid: "Valid",
    errors: "Errors",
    warnings: "Warnings",
    filter: "Filter:",
    all: "All",
    error: "Error",
    warning: "Warning",
    info: "Info",
    group: "Group:",
    byCode: "Code",
    byColumn: "Column",
    byRow: "Row",
    issues: "{count} issue(s)",
    rows: "Rows:",
    noIssues: "No issues with the current filter.",
    value: "Value:",
    rowLabel: "Row {row}",
  },
  preview: {
    status: "Status",
    showing: "Showing {count} row(s)",
    valid: "{count} valid",
    warning: "{count} warning",
    error: "{count} error",
    noData: "No data to preview.",
    backToData: "‹ Data & Group",
    dataLabel: "Data",
    noExcel: "No Excel",
    templateLabel: "Template",
    noTemplate: "No template",
    paperLabel: "Paper",
    willPrintLabel: "Will print",
    pages: "{count} page(s)",
    collapsePanel: "Collapse panel",
    expandPanel: "Expand panel",
  },
  printWizard: {
    steps: {
      upload: "Upload",
      design: "Design",
      bind: "Bind Columns",
      rows: "Row Mapping",
      preview: "Preview & Export",
    },
    uploadTitle: "Upload Excel File",
    paperSize: "Paper size:",
    uploadDesc:
      "Upload an Excel file (.xlsx, .xls, .csv). The system will automatically read all sheet names and column headers to build the template.",
    parsingFile: "Parsing file...",
    dropExcel: "Drop your Excel file here",
    orBrowseFormats: "or click to browse — .xlsx, .xls, .csv",
    sheetToUse: "Sheet to use:",
    sheetMeta: "{name} ({rows} rows, {cols} cols)",
    columnsRows: "{cols} columns · {rows} rows",
    moreColumns: "+{count} more",
    orImportTemplate: "or import a saved template:",
    loadTemplateJson: "Load Template JSON",
    continueToDesign: "Continue to Design",
    designTitle: "Design Template",
    columnsFromFile: "{count} columns from file",
    regenerate: "Regenerate",
    changeFile: "Change File",
    continueToBind: "Continue to Bind Columns",
    bindTitle: "Bind Template Fields to Excel Columns",
    sheetBadge: "Sheet: {name}",
    tablePage: "Page",
    tableField: "Field",
    tableType: "Type",
    tableBoundColumn: "Bound Column",
    tableTransform: "Transform",
    tableFallback: "Fallback",
    selectColumn: "Select column...",
    transformNone: "None",
    transformUpper: "UPPERCASE",
    transformLower: "lowercase",
    transformTrim: "Trim spaces",
    fallbackPlaceholder: "fallback",
    unboundFields: "{count} unbound field(s)",
    unboundDesc: "{fields} will appear empty in the printed output.",
    andMore: " and {count} more",
    continueToRowMapping: "Continue to Row Mapping",
    rowMappingTitle: "Row → Page Mapping",
    splitByColumn: "Split Page by Column",
    splitDesc:
      "If a row has multiple items (e.g., 3 packages), split it into multiple pages — one per item (1/3, 2/3, 3/3).",
    splitColumn: "Split column:",
    noSplit: "— No split —",
    noSplitPlaceholder: "No split — one page per row",
    willSplitBy: "Rows will be split by: {column}",
    splitPreview: "Split preview:",
    splitItems: "{n} items: {rows} row(s) → {pages} page(s)",
    pagesPerRecord: "Pages per record:",
    maxPages: "(max: {max})",
    previewFirstRows: "Preview — first 3 rows mapped to pages",
    summary: "Summary:",
    summaryWithData: "{pages} total pages from {rows} rows{split}",
    withSplit: " (with split)",
    noDataRows: "No data rows available",
    continueToPreview: "Continue to Preview",
    previewExportTitle: "Preview & Export",
    editTemplate: "Edit Template",
    noDataTitle: "No Data",
    noDataDesc:
      "The Excel file has no data rows to print. Upload a file with data in the first step.",
    pleaseUploadFirst: "Please upload an Excel file first.",
    invalidTemplate: "Invalid template file. Expected JSON.",
    failedParse: "Failed to parse file",
    printName: "Print – {name}",
    pageLabel: "Page {n}",
  },
  canvas: {
    tools: {
      select: "Select & move objects",
      text: "Click to add a text field",
      line: "Click-drag to draw a line",
      checkbox: "Click to add a checkbox",
      label: "Click to add a static label",
      pan: "Pan the canvas",
    },
    textField: "Text field",
    label: "Label",
    switchLandscape: "Switch to Landscape",
    switchPortrait: "Switch to Portrait",
    zoomOut: "Zoom out",
    zoomIn: "Zoom in",
    fit: "Fit",
    resetZoom: "Reset to 100%",
    pageOf: "Page {current} / {total}",
    addPage: "Add Page",
    templateProperties: "Template Properties",
    templateName: "Template Name",
    pagesPerRecord: "Pages per Record",
    sheetFields: "Sheet Fields",
    filterColumns: "Filter columns...",
    clickColumnHint: "Click a column to add it as a bound field on the canvas",
    fieldProperties: "Field Properties",
    fieldLabel: "Field Label",
    type: "Type",
    fontSize: "Font Size",
    lineHeight: "Line Height",
    color: "Color",
    fontFamily: "Font Family",
    textAlign: "Text Align",
    boundColumn: "Bound Column",
    transform: "Transform",
    fallbackValue: "Fallback Value",
    shownWhenEmpty: "Shown when empty",
    selectObject: "Select an object on the canvas to edit its properties",
    templateFile: "Template File",
    importTemplate: "Import Template",
    exportTemplate: "Export Template",
    untitled: "Untitled Template",
  },
  printPreview: {
    prev: "Prev",
    next: "Next",
    pageOf: "Page {current} of {total}",
    row: "Row {n}",
    generating: "Generating...",
    downloadPdf: "Download PDF",
    print: "Print",
    unboundFields: "Unbound Fields",
    unboundDesc:
      "{count} field(s) on page(s) {details} — no column bound. These will appear empty.",
    noPagesTitle: "No pages to preview",
    noPagesDesc: "Add data rows and ensure your template has bound fields.",
    footerSummary:
      "{rows} row(s) × {pagesPerRecord} page(s)/row = {total} total pages",
    allBound: "All fields bound",
    unboundCount: "{count} unbound",
    pdfFailed: "PDF generation failed",
    printFailed: "Print failed",
  },
  validator: {
    required: '"{header}" is required.',
    duplicateWithFirst: 'Duplicate value "{value}" – first seen on row {row}.',
    duplicate: 'Duplicate value "{value}".',
    pattern: '"{value}" does not match required pattern: {pattern}',
    notANumber: '"{value}" is not a valid number.',
    minValue: "Value must be at least {min}, got {num}.",
    maxValue: "Value must be at most {max}, got {num}.",
    invalidDate: '"{value}" is not a valid date.',
    enum: '"{value}" is not in the allowed list: {list}.',
  },
  parser: {
    couldNotRead:
      "Could not read file. It may be corrupted or not a valid Excel/CSV file.",
    noSheets: "Workbook contains no sheets.",
  },
  recentJobs: {
    title: "Recent print jobs",
    clearAll: "Clear all",
    empty: "No print/export job has been recorded yet.",
    remove: "Remove this entry",
    template: "Template",
  },
  toasts: {
    excelLoaded: "Excel loaded successfully",
    excelLoadFailed: "Excel load failed",
    templateLoaded: "Template loaded",
    templateParseFailed: "Template parse failed",
    renderDone: "Preview rendered",
    renderFailed: "Preview render failed",
    exportPdfSingle: "Export current page PDF",
    exportPdfAll: "Export all pages PDF",
    exportZip: "Export one PDF per ticket (ZIP)",
    print: "Print",
    exportOk: "Export successful",
    exportFailed: "Export failed",
    printStarted: "Opening print dialog…",
    printFailed: "Print failed",
    pagesOverflow: "{count} page(s) overflow (will auto-shrink)",
    fieldsUnbound: "{count} field(s) not bound to a column",
  },
  dataStep: {
    uploadTitle: "Upload Data",
    uploadDesc: "Choose an Excel file, then pick the sheet to process.",
    changeFile: "Change Excel File",
    uploadLabel: "Upload Data",
    dropHint: "Drop a file here or click to choose (.xlsx, .xls, .csv)",
    roleTitle: "Workbook macro type detected",
    sheetLabel: "Data sheet (bind source)",
    roleData: " · data",
    rolePrint: " · Excel print template",
    colsRows: "{cols} col(s) · {rows} row(s)",
    groupTitle: "Group Data",
    groupDesc: "Choose the <strong>main grouping column</strong> — each group becomes one document (print page).",
    waitingData: "Waiting for data",
    waitingDesc: "Upload the Excel file on the left, pick a sheet, then choose a grouping column to create documents.",
    noGroup: "-- Not selected --",
    groupPlaceholder: "Choose main grouping column",
    searchIn: "Search in {count} column(s)…",
    confirmChangeGroup: "Changing the main grouping column will refresh the print page list (losing current selections). Continue?",
    groupExample: "E.g. choose <code>Delivery point code</code> so each delivery point becomes one print ticket.",
    rowsToDocs: "{rows} row(s) → {groups} document(s)",
    oneDocOnePage: "(1 document = 1 print page)",
    rowsCount: "{count} row(s)",
    moreDocs: "+ {count} more document(s)…",
    lookupTitle: "Pull Extra Data From Secondary Sheet",
    lookupDesc: "Main sheet <strong>{sheet}</strong> is the bind source. If the template needs fields in another sheet, pick the secondary sheet and join key — the system only suggests same-name columns, you must confirm.",
    noLookup: "No secondary sheet",
    noLookupItem: "-- No secondary sheet --",
    joinBy: "join by",
    primaryCol: "Column on main sheet",
    secondaryCol: "Column on secondary sheet",
    joinCandidates: "{count} same-name candidate column(s) ({cols}) — confirm the correct join key.",
    joinIncomplete: "Join keys not fully selected — fields from the secondary sheet are not usable yet.",
    joinedCols: "Added {count} column(s) from secondary sheet: {cols}",
    readyForTemplate: "Group selected · ready to move to template",
    chooseGroup: "Choose a main grouping column to continue",
    uploadFirst: "Upload Excel before continuing",
    continuePreview: "Continue: Preview & Print",
  },
  picker: {
    title: "Template library",
    desc: "Pick a system template or manage your .docx library. Add files to Mine, tag them, then click Use this template when printing.",
    searchPlaceholder: "Search templates by name or description…",
    dropTitle: "Drop a .docx anywhere in the library, or click to choose",
    dropText: "Drop a <code class=\"rounded bg-muted px-1\">.docx</code> anywhere · or click to add to library",
    dropOverlay: "Drop .docx files to add to library",
    dropOverlayHint: "You can drop multiple files at once",
    tabAll: "All",
    tabSystem: "System templates",
    tabMy: "Mine",
    navLibrary: "Library",
    addToLibrary: "Add .docx",
    addToLibraryHint: "Save to My library (does not change the active template)",
    libraryCount: "{count} templates",
    allCount: "All ({count})",
    addTagTitle: "Add tag",
    addTag: "+ Tag",
    saveCurrent: "Save current template as my template",
    emptyTitle: "Library is empty",
    emptyDesc: "Add a .docx to manage, tag, and reuse when printing documents.",
    sectionMy: "Mine",
    sectionSystem: "System",
    noMatch: "No template matches \"{query}\".",
    useThis: "Use this template",
    preview: "Preview",
    previewBig: "Preview {name} larger",
    downloadFile: "Download {name}",
    footer: "{count} templates · Preview · Use this template",
    close: "Close",
    downloadDocx: "Download .docx",
    useTemplate: "Use template {name}",
    editedBadge: "Edited",
    badgeSystem: "System",
    badgeMine: "Mine",
    selectToPreview: "Select a template to preview details",
    backToList: "Back to list",
    newTagPlaceholder: "New tag name",
    rename: "Rename",
    renameTemplate: "Rename template {name}",
    deleteTemplate: "Delete template",
    deleteTemplateName: "Delete template {name}",
    untag: "Remove tag {name}",
    tag: "Add tag {name}",
    untagFrom: "Remove tag {name} from {tpl}",
    tagTo: "Add tag {name} to {tpl}",
    addedToLibrary: "Added to library",
  },
  editor: {
    close: "Close",
    saveAndPreview: "Save & View Result",
    splitPage: "Split page",
    newText: "New text",
    addedText: "Text added",
    addedTextDesc: "Drag to position, select to edit content and formatting.",
    insertedImage: "Image inserted",
    insertedImageDesc: "Image is embedded as base64 so it stays intact when printing/PDF.",
    deleted: "Deleted",
    deletedDesc: "The element was removed from the page.",
    pasteHint: "Paste (Ctrl+V) into the Word canvas",
    reloadConfirm: "Reload original template?\n\nAll unsaved changes on this page (text, tables, data) will be lost. Continue?",
    reloaded: "Original template reloaded",
    zoomOut: "Zoom out",
    resetZoom: "Reset to 100%",
    zoomIn: "Zoom in",
    editOn: "Edit",
    editOff: "Turn off editing",
    reloadTemplate: "Reload original template",
    commitEdits: "Write canvas edits into the Word file",
    downloadDocx: "Download .docx",
    openPanel: "Open panel",
    collapsePanel: "Collapse panel",
    lockAndPreview: "lock & go to preview",
  },
  tour: {
    back: "Back",
    next: "Next",
    skip: "Skip",
    finish: "Done",
    close: "Close",
    replay: "Quick tour",
    openDocs: "Full guide",
    menuTitle: "Help",
    dataWelcomeTitle: "Welcome to Document Printing",
    dataWelcomeBody: "Build business documents from Excel + a Word template in 2 steps: prepare data, then preview & print.",
    dataExcelTitle: "1. Upload Excel",
    dataExcelBody: "Drop or pick an .xlsx file. Sheets and columns are detected automatically.",
    dataGroupTitle: "2. Grouping column",
    dataGroupBody: "Pick a grouping column (e.g. PO number) — each value becomes one printable document.",
    dataContinueTitle: "3. Continue to preview",
    dataContinueBody: "When Excel and grouping are ready, continue to pick a template and print/PDF.",
    dataHelpTitle: "Need more help?",
    dataHelpBody: "Open Help anytime to replay this quick tour or read the full guide.",
    previewWelcomeTitle: "Step 2 — Preview & Print",
    previewWelcomeBody: "Choose a template, paper size, tick documents to print, then export PDF / print / ZIP.",
    previewTemplateTitle: "Template library",
    previewTemplateBody: "Open Template for All · Mine · System presets. Drop a .docx to add your own.",
    previewPaperTitle: "Paper & printer",
    previewPaperBody: "Pick A4/A5… and Portrait/Landscape. Save printer profiles for reuse.",
    previewListTitle: "Print list",
    previewListBody: "Enable/disable each document. Only selected ones go to PDF/Print/ZIP.",
    previewExportTitle: "Export & Print",
    previewExportBody: "Export PDF (page/all), browser print, or ZIP per document. You're ready to go!",
  },
};
